import type {
  IScriptTemplateQuestion,
  IScriptTemplateQuestionAnalytics,
  ScoreBadgeTier,
  StatCardTone,
} from "@shadowcue/ui";

/**
 * Minimum AI-rated responses before a question's effectiveness is treated as
 * meaningful. Mirrors MIN_RESPONSES_TO_FLAG in
 * app/services/script_template_analytics_service.rb — keep the two in sync.
 *
 * Below this, Postgres STDDEV_POP collapses to 0 (it returns 0, not NULL, for a
 * single row), so the differentiation half of the effectiveness score is
 * structurally suppressed and the number is an artifact rather than a signal.
 */
export const MIN_RESPONSES_FOR_EFFECTIVENESS = 5;

/** Effectiveness percentage at or above which a question reads as healthy. */
export const EFFECTIVENESS_HIGH_PCT = 60;

/**
 * Effectiveness percentage below which the backend flags a question as weak.
 * Mirrors WORTH_REVIEWING_THRESHOLD (0.30) in the analytics service.
 */
export const EFFECTIVENESS_LOW_PCT = 30;

/**
 * Converts a 0–1 ratio from the analytics service into a 0–100 integer for
 * display. Every percentage on this page goes through here so that the value
 * shown on a chip, the value shown on a card, and the tier used to color them
 * can never disagree by a rounding step.
 */
export const toPercent = (ratio: number | null | undefined): number | null => {
  if (ratio == null) return null;
  return Math.round(ratio * 100);
};

/** True when a question has enough responses for its metrics to be trusted. */
export const hasEnoughResponses = (
  analytics: IScriptTemplateQuestionAnalytics | undefined,
): boolean =>
  analytics != null &&
  analytics.totalResponses >= MIN_RESPONSES_FOR_EFFECTIVENESS;

/**
 * Mean effectiveness across the questions that have enough responses to count,
 * as a 0–100 integer. Returns null when no question qualifies, so callers
 * render an em dash rather than dividing by zero.
 *
 * Thin-data questions are excluded deliberately: their suppressed spread would
 * drag the headline number down without reflecting question quality.
 */
export const meanEffectivenessPct = (
  questions: IScriptTemplateQuestion[],
): number | null => {
  const scores = questions
    .filter((question) => hasEnoughResponses(question.analytics))
    .map((question) => question.analytics?.effectivenessScore)
    .filter((score): score is number => score != null);

  if (scores.length === 0) return null;

  const mean = scores.reduce((sum, score) => sum + score, 0) / scores.length;
  return toPercent(mean);
};

/**
 * Badge tier for an effectiveness percentage.
 *
 * The order here is load-bearing:
 *   1. Too few responses reads muted — the number is shown but not judged.
 *   2. A question the backend flagged always reads red, even if its rounded
 *      percentage lands on the amber boundary (a raw 0.2996 displays as 30%).
 *      This is what keeps every red chip in step with the Weak Questions count.
 *   3. Everything else bands on the displayed percentage.
 */
export const effectivenessTier = (
  pct: number | null,
  analytics?: IScriptTemplateQuestionAnalytics,
): ScoreBadgeTier => {
  if (analytics && !hasEnoughResponses(analytics)) return "muted";
  if (analytics?.worthReviewing) return "low";
  if (pct == null) return "muted";
  if (pct < EFFECTIVENESS_LOW_PCT) return "low";
  if (pct < EFFECTIVENESS_HIGH_PCT) return "mid";
  return "high";
};

/** Stat card tone for an effectiveness percentage. */
export const effectivenessTone = (pct: number | null): StatCardTone => {
  if (pct == null) return "neutral";
  if (pct < EFFECTIVENESS_LOW_PCT) return "critical";
  if (pct < EFFECTIVENESS_HIGH_PCT) return "warning";
  return "positive";
};

type AvgScoreTier = "high" | "mid" | "low";

/**
 * Tier for a raw average score against its scale maximum.
 *
 * Deliberately named apart from effectivenessTier: this one takes a raw score
 * plus its max, that one takes an already-computed percentage. Passing a
 * percentage in here would return "high" for almost any input.
 */
export const avgScoreTier = (score: number, maxScore: number): AvgScoreTier => {
  const ratio = score / maxScore;
  if (ratio >= 0.8) return "high";
  if (ratio >= 0.4) return "mid";
  return "low";
};

export const AVG_SCORE_TONE: Record<AvgScoreTier, StatCardTone> = {
  high: "positive",
  mid: "warning",
  low: "critical",
};

/** Every question across every section, flattened. */
export const flattenQuestions = (
  sections: { scriptTemplateQuestions: IScriptTemplateQuestion[] }[],
): IScriptTemplateQuestion[] =>
  sections.flatMap((section) => section.scriptTemplateQuestions);
